import React, { useEffect, useRef, useState } from 'react';

interface SlideContent {
    id: string;
    number: string;
    heading: string;
    description: string;
    image: string;
}

interface KeyFindingsData {
    sectionId: string;
    tagline: string;
    heading: string;
    description: string;
    slides: SlideContent[];
}

interface KeyFindingsProps {
    data: KeyFindingsData;
}

const KeyFindingsSection: React.FC<KeyFindingsProps> = ({
    data
}) => {
    const [currentSlide, setCurrentSlide] = useState(0);
    const totalSlides = data.slides.length;
    const [slideWidth, setSlideWidth] = useState(0);
    const slideRef = useRef<HTMLDivElement | null>(null);
    const touchStartX = useRef(0);
    const isDragging = useRef(false);
    const [offsetX, setOffsetX] = useState(0);

    const handleNextSlide = () => {
        if (currentSlide < totalSlides - 1) {
            setCurrentSlide((prevSlide) => prevSlide + 1);
            setOffsetX(0);
        }
    };

    const handlePrevSlide = () => {
        if (currentSlide > 0) {
            setCurrentSlide((prevSlide) => prevSlide - 1);
            setOffsetX(0);
        }
    };

    const updateSlideWidth = () => {
        if (slideRef.current) {
            const width = slideRef.current.clientWidth;
            setSlideWidth(width);
        }
    };

    const handleTouchStart = (e: React.TouchEvent) => {
        const touch = e.touches[0];
        if (touch) {
            touchStartX.current = touch.clientX;
            isDragging.current = true;
        }
    };

    const handleTouchMove = (e: React.TouchEvent) => {
        if (!isDragging.current) return;
        const touch = e.touches[0];
        if (touch) {
            const touchMoveX = touch.clientX;
            const deltaX = touchMoveX - touchStartX.current;

            // İlk slayttayken sola, son slayttayken sağa fazla kaymayı sınırlıyoruz
            if ((currentSlide === 0 && deltaX > 0) || (currentSlide === totalSlides - 1 && deltaX < 0)) {
                setOffsetX(deltaX / 4); // Sınırlı kaydırma efekti için direnç ekliyoruz
            } else {
                setOffsetX(deltaX);
            }
        }
    };

    const handleTouchEnd = () => {
        isDragging.current = false;

        if (offsetX < -slideWidth / 4 && currentSlide < totalSlides - 1) {
            handleNextSlide();
        } else if (offsetX > slideWidth / 4 && currentSlide > 0) {
            handlePrevSlide();
        } else {
            setOffsetX(0); // Yetersiz kaydırma yapıldığında geri ortalar
        }
    };

    useEffect(() => {
        updateSlideWidth();
        window.addEventListener('resize', updateSlideWidth);

        return () => {
            window.removeEventListener('resize', updateSlideWidth);
        };
    }, []);

    return (
        <section id={data.sectionId} className="master-section">
            <div className="section-intro medium-cream background">
                <div className="section-intro-header">
                    <h1 className="number-display dark">
                        <span className="fontawesome"></span>
                    </h1>
                    <div className="limited-heading-set">
                        <div className="tagline-heading">{data.tagline}</div>
                        <h2 className="heading-1">{data.heading}</h2>
                        <p className="paragraph-large">{data.description}</p>
                    </div>
                </div>
            </div>

            <section className="key-findings-section section-regular background-cream postintro">
                <div className="testimonial-slider-container">
                    <div className="testimonial-slider w-slider"
                        onTouchStart={handleTouchStart}
                        onTouchMove={handleTouchMove}
                        onTouchEnd={handleTouchEnd}
                    >
                        <div className="team-slider-mask w-slider-mask">
                            {data.slides.map((slide, index) => {
                                const isActive = index === currentSlide;

                                return (
                                    <div key={slide.id}
                                        className={`testimonial-slide wide slide w-slide ${isActive ? 'active' : ''}`}
                                        style={{
                                            transform: `translateX(${-currentSlide * slideWidth + offsetX}px)`,
                                            transition: isDragging.current ? 'none' : 'transform 500ms ease',
                                            opacity: isActive ? 1 : 1,
                                        }}
                                        ref={index === 0 ? slideRef : null}
                                    >
                                        <div className="wide-slider">
                                            <div className="keyfindings-card">
                                                <div className="header-row">
                                                    <div className="keyfinding-visual-holder">
                                                        <img
                                                            src={slide.image}
                                                            loading="lazy"
                                                            alt={slide.heading}
                                                            className="card-visual"
                                                        />
                                                    </div>
                                                    <h2 className="number-display cream">{slide.number}</h2>
                                                </div>
                                                <div className="keyfindings-text-holder">
                                                    <div className="h4-heading">{slide.heading}</div>
                                                    <div className="paragraph-x-large text-color-gray-600">
                                                        {slide.description}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )
                            }
                            )}
                        </div>
                        <div
                            className="generic-slider-trigger left cream w-slider-arrow-left"
                            onClick={handlePrevSlide}
                            aria-hidden={currentSlide === 0 ? "true" : "false"}
                        >
                            <div className="slider-chevron"></div>
                        </div>
                        <div
                            className="generic-slider-trigger right cream w-slider-arrow-right"
                            onClick={handleNextSlide}
                            aria-hidden={currentSlide === totalSlides - 1 ? "true" : "false"}
                        >
                            <div className="slider-chevron"></div>
                        </div>
                        <div className="slide-nav-hidden w-slider-nav w-round w-num"></div>
                    </div>
                </div>
            </section>
        </section>
    );
};

export default KeyFindingsSection;
